import React, {useCallback, useEffect, useRef, useState} from "react";
import {Select} from "antd";
import "./UserSelect.less";
import _ from "lodash";
import {CloseOutlined} from "@ant-design/icons";
import ArchiveService from "../../service/emp/archive/ArchiveService";
import BusinessConstants from "../../constant/BusinessConstants";
import PromiseUtil from "../../util/PromiseUtil";
import UserTree from "./UserTree";
import TreeUtil from "../../util/TreeUtil";
import Avatar from "../avatar/Avatar";
import Text from "../text/Text";
import AppContext from "../../application/AppContext";
import WsjwSettingService from "../../service/wsjkw/setting/WsjswSettingService";

const filterEmptyChildrenNode = (arr) => {
    return _.chain(arr)
        .map((it) => {
            if (it.type === "UNIT") {
                const children = filterEmptyChildrenNode(it.children);
                if (_.isEmpty(children)) return null;
                return {
                    ...it,
                    children,
                };
            }
            return it;
        })
        .compact()
        .value();
};

const filterTreeUser = (tree, userIds, visible = true) => {
    const _filterUser = (arr) => {
        return _.chain(arr)
            .map((it) => {
                if (it.type === "USER" && (visible ? _.includes(userIds, it.userId) : !_.includes(userIds, it.userId))) {
                    return it;
                } else if (it.type === "UNIT") {
                    return {
                        ...it,
                        children: _filterUser(it.children),
                    };
                }
                return null;
            })
            .compact()
            .value();
    };

    const filteredUserTree = _filterUser(tree);
    return filterEmptyChildrenNode(filteredUserTree);
};


const getFilterUserIds = (tree, filterUnitIds, includes = false) => {
    let userIds = [];
    const _getUsers = (arr) => {
        _.forEach(arr, (it) => {
            if (it.type === "UNIT") {
                if (!includes) {
                    if (_.includes(filterUnitIds, it.unitId)) {
                        const users = _.filter(it.children, child => child.type === "USER");
                        userIds = _.concat(userIds, users);
                    }
                } else {
                    if (!_.includes(filterUnitIds, it.unitId)) {
                        const users = _.filter(it.children, child => child.type === "USER");
                        userIds = _.concat(userIds, users);
                    }
                }
                _getUsers(it.children);
            }
        });
    };
    _getUsers(tree);
    return _.chain(userIds)
        .map(it => it.userId)
        .uniq()
        .value();
};

const getTreeData = (employeeTree, idToKeyMap, unitId) => {
    return _.map(employeeTree, (it) => {
        if (it.type === "USER") {
            const user = {
                id: it.userId,
                name: it.userName,
            };

            const key = `user-${unitId ?? ""}-${user.id}`;
            if (idToKeyMap) {
                if (idToKeyMap[user.id] == null) {
                    idToKeyMap[user.id] = [key];
                } else {
                    idToKeyMap[user.id].push(key);
                }
            }

            return {
                isUser: true,
                title: (
                    <div
                        style={{
                            display: "flex",
                            alignItems: "center",
                        }}
                    >
                        <Avatar
                            height={32}
                            src={decodeURI(it.userAvatar ?? "")}
                            name={it.userName}
                            transition={false}
                        />
                        <div
                            style={{
                                flex: "none",
                                marginLeft: 8,
                            }}
                        >
                            {it.userName}
                        </div>
                    </div>
                ),
                key,
                item: user,
            };
        } else {
            return {
                isUnit: true,
                title: it.unitName,
                key: `unit-${it.unitId}`,
                children: getTreeData(it.children, idToKeyMap, it.unitId),
            };
        }
    });
};

const getAllUsers = async (idToKeyMapRef, includesUnitIds) => {
    const fn = AppContext.isSwjw
        ? WsjwSettingService.findUserTreeAsync
        : ArchiveService.findBasicEmployeeTreeAsync;

    let data = await fn({
        /*  userType: BusinessConstants.USER_TYPE.EMPLOYEE.code, */
        unitTypes: _.map(BusinessConstants.UNIT_TYPE, (__, key) => key),
    });
    if (!_.isEmpty(includesUnitIds)) {
        const _employeeTree = [];
        _.forEach(includesUnitIds, (unitId) => {
            const node = TreeUtil.getNode(data, unitId, "unitId");
            if (node) {
                _employeeTree.push(node);
            }
        });
        data = _employeeTree;
    }
    return getTreeData(data, idToKeyMapRef.current);
};

const searchUsers = async (keyword, idToKeyMapRef) => {
    const fn = AppContext.isSwjw
        ? WsjwSettingService.findUserTreeAsync
        : ArchiveService.findBasicEmployeeTreeAsync;
    const data = await fn({
        /* userType: BusinessConstants.USER_TYPE.EMPLOYEE.code, */
        unitTypes: _.map(BusinessConstants.UNIT_TYPE, (__, key) => key),
        keyword,
    });
    idToKeyMapRef.current = {};
    return getTreeData(data, idToKeyMapRef.current);
};

const UserSelect = ({
    style,
    dropdownStyle,
    unitId,
    includesUnitIds,
    value,
    onChange,
}) => {
    const [loading, setLoading] = useState(false);
    const [treeData, setTreeData] = useState(null);
    const idToKeyMap = useRef({});
    const loadPromise = useRef(null);

    useEffect(() => {
        loadPromise.current = PromiseUtil.makeTrashable(getAllUsers(idToKeyMap, includesUnitIds));
        setLoading(true);
        loadPromise.current.then((data) => {
            setTreeData(data);
            setLoading(false);
        });
        return () => {
            loadPromise.current?.trash();
        };
    }, []);

    const onSearch = useCallback((searchValue) => {
        setLoading(true);
        loadPromise.current = PromiseUtil.makeTrashable(searchUsers(searchValue, idToKeyMap));
        loadPromise.current.then((data) => {
            setTreeData(data);
            setLoading(false);
        });
    }, []);

    const getCheckedKeysFromItem = useCallback((item) => {
        return idToKeyMap.current?.[item.id];
    }, [idToKeyMap]);

    const getCheckedNodesFromNode = useCallback((node) => {
        if (node.isUser) {
            return [node];
        }
        return TreeUtil.filterNodes([node], it => it.isUser);
    }, []);

    return (
        <Select
            mode="multiple"
            className="renyuan-user-select"
            placeholder="请选择"
            style={{
                width: 504,
                ...style,
            }}
            showArrow
            onInputKeyDown={(e) => {
                // 禁用输入
                e.preventDefault();
                e.stopPropagation();
            }}
            loading={loading}
            labelInValue
            maxTagCount={10}
            maxTagPlaceholder={(omittedValues) => {
                return ` +${omittedValues.length}...`;
            }}
            value={_.map(value, (it) => {
                return {
                    label: it.name,
                    value: it.id,
                };
            })}
            tagRender={(props) => {
                const {value: tagValue, label} = props;
                return (
                    <div
                        onMouseDown={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                        }}
                        style={{
                            backgroundColor: "#F3F6F8",
                            borderRadius: 14,
                            height: 28,
                            padding: "0 12px",
                            display: "flex",
                            alignItems: "center",
                            maxWidth: 100,
                            margin: 4,
                            marginLeft: 0,
                        }}
                    >
                        <div style={{flex: 1, display: "flex", alignItems: "center"}}>
                            <Text>
                                {label}
                            </Text>
                        </div>
                        <div
                            style={{flex: "none", fontSize: 10, marginLeft: 8, cursor: "pointer", color: "#90A4AD"}}
                            onClick={(e) => {
                                onChange(_.filter(value, it => it.id !== tagValue));
                            }}
                        >
                            <CloseOutlined />
                        </div>
                    </div>
                );
            }}
            dropdownRender={() => {
                return (
                    <UserTree
                        treeData={treeData}
                        getCheckedKeysFromItem={getCheckedKeysFromItem}
                        defaultExpandedKeys={_.map(includesUnitIds, it => `unit-${it}`)}
                        getCheckedNodesFromNode={getCheckedNodesFromNode}
                        style={dropdownStyle}
                        value={value}
                        onChange={onChange}
                        loading={loading}
                        onSearch={onSearch}
                    />
                );
            }}
        />
    );
};

export default UserSelect;
